import { ApplicationTypes } from 'metalworks-domain'
import type { PropsWithChildren } from 'react'
import { useEffect, useMemo, useRef } from 'react'
import { BehaviorSubject } from 'rxjs'

import { Loader } from '@salsita-ai/platform-project-sdk/ui'

import { MetalworksInitWrapper, clearCloneUrlParam } from '../metalworks-init.js'
import { anchorPointsService } from '../systems/anchor-points-service.js'
import { configuratorInterface } from '../systems/configurator-interface.js'
import { MetalworksApiProvider } from '../systems/metalworks-api.js'
import { useCloneConfiguration } from './hooks/use-clone-configuration.js'
import { AppInvariantViolations } from './violations-fix.js'

const DimensionIndicatorShownDefaultMap = new Map<ApplicationTypes.NavigationState, boolean>([
  [ApplicationTypes.NavigationState.Dimensions, true],
  [ApplicationTypes.NavigationState.Features, true],
  [ApplicationTypes.NavigationState.FeatureList, true],
  [ApplicationTypes.NavigationState.Summary, false],
  [ApplicationTypes.NavigationState.Finalization, true],
  [ApplicationTypes.NavigationState.BulkFinalization, true],
])

/**
 * Hack until sdk support configurator navigation on integration level
 */
export const GlobalHackyConfiguratorOpenedSubject = new BehaviorSubject<'open' | 'closed'>('closed')

export const CustomOnLoadProvider = (props: PropsWithChildren) => {
  const { children } = props

  const { canvasSystem } = configuratorInterface.useConfigurator()
  const activeStateTag = configuratorInterface.useActiveNavigationState()
  const { cloneAndStart } = useCloneConfiguration()
  const isClonedRef = useRef(false)

  const hasCloneParam = useMemo(() => {
    return window.location.search.includes('clone')
  }, [])

  useEffect(() => {
    if (hasCloneParam && !isClonedRef.current) {
      isClonedRef.current = true
      cloneAndStart()
      clearCloneUrlParam()
    }
  }, [hasCloneParam, cloneAndStart])

  // Hide anchor points when navigating between steps.
  useEffect(() => {
    anchorPointsService.setVisible(false)
  }, [activeStateTag])

  useEffect(() => {
    GlobalHackyConfiguratorOpenedSubject.next('open')

    return () => {
      GlobalHackyConfiguratorOpenedSubject.next('closed')
    }
  }, [])

  /**
   * Set dimension indicator state appropriately when navigating.
   */
  useEffect(() => {
    const defaultDimensionIndicatorShown = !!DimensionIndicatorShownDefaultMap.get(activeStateTag)
    canvasSystem.setDimensionIndicatorShown(defaultDimensionIndicatorShown)
  }, [activeStateTag, canvasSystem])

  if (hasCloneParam) {
    return <Loader />
  }

  return (
    <MetalworksInitWrapper>
      <MetalworksApiProvider>
        <AppInvariantViolations />
        {children}
      </MetalworksApiProvider>
    </MetalworksInitWrapper>
  )
}
